import { useMemo } from "react";
import type { World } from "@cimara/world";

interface CourseMapProps {
  world: World | null;
  routeId?: number;
  label?: string;
}

interface MapShape {
  path: string;
  start: { x: number; y: number } | null;
}

const PLACEHOLDER_PATH =
  "M 12 68 C 18 30, 36 22, 50 38 C 64 54, 62 78, 78 80 C 92 82, 92 42, 82 28 C 72 14, 52 12, 42 22";

function buildShape(world: World | null, routeId: number | undefined): MapShape {
  if (!world) return { path: PLACEHOLDER_PATH, start: null };

  const route =
    (routeId === undefined ? undefined : world.getRoute(routeId)) ?? world.routes[0];
  if (!route) return { path: PLACEHOLDER_PATH, start: null };

  const points: Array<{ x: number; z: number }> = [];
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;

  for (const edgeId of route.edgeIds) {
    const edge = world.getEdge(edgeId);
    if (!edge) continue;
    const pointCount = edge.points.length / 3;
    for (let i = 0; i < pointCount; i += 8) {
      const x = edge.points[i * 3];
      const z = edge.points[i * 3 + 2];
      points.push({ x, z });
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minZ = Math.min(minZ, z);
      maxZ = Math.max(maxZ, z);
    }
  }

  if (points.length === 0 || !Number.isFinite(minX) || !Number.isFinite(minZ)) {
    return { path: PLACEHOLDER_PATH, start: null };
  }

  const width = Math.max(1, maxX - minX);
  const depth = Math.max(1, maxZ - minZ);
  const scale = Math.min(88 / width, 86 / depth);
  const centreX = (minX + maxX) / 2;
  const centreZ = (minZ + maxZ) / 2;
  const project = (x: number, z: number): { x: number; y: number } => ({
    x: 50 + (x - centreX) * scale,
    y: 50 - (z - centreZ) * scale,
  });

  const path = points
    .map((point, index) => {
      const projected = project(point.x, point.z);
      return `${index === 0 ? "M" : "L"} ${projected.x.toFixed(2)} ${projected.y.toFixed(2)}`;
    })
    .join(" ");

  const startEdge =
    routeId !== undefined && route.edgeIds.length > 0 ? route.edgeIds[0] : world.spawn.edge;
  const startS = routeId !== undefined ? 0 : world.spawn.s;
  const spawn = world.samplePosition(startEdge, startS);
  return { path: `${path} Z`, start: project(spawn.x, spawn.z) };
}

/**
 * A tiny vector map of the circuit, drawn from the same binary world the
 * ride uses. No external map tiles or art assets.
 */
export function CourseMap({ world, routeId, label }: CourseMapProps) {
  const shape = useMemo(() => buildShape(world, routeId), [world, routeId]);
  const route = world
    ? (routeId === undefined ? undefined : world.getRoute(routeId)) ?? world.routes[0]
    : null;

  return (
    <div className="course-map" aria-hidden="true">
      <svg viewBox="0 0 100 100" role="presentation">
        <defs>
          <linearGradient id="courseFade" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#d8ff57" stopOpacity="0.95" />
            <stop offset="70%" stopColor="#56e0b4" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#56e0b4" stopOpacity="0.15" />
          </linearGradient>
        </defs>
        <g className="course-map__grid">
          <line x1="0" y1="25" x2="100" y2="25" />
          <line x1="0" y1="50" x2="100" y2="50" />
          <line x1="0" y1="75" x2="100" y2="75" />
          <line x1="25" y1="0" x2="25" y2="100" />
          <line x1="50" y1="0" x2="50" y2="100" />
          <line x1="75" y1="0" x2="75" y2="100" />
        </g>
        <path className="course-map__halo" d={shape.path} />
        <path className="course-map__line" d={shape.path} />
        {shape.start ? (
          <g className="course-map__start">
            <circle cx={shape.start.x} cy={shape.start.y} r="4.2" />
            <circle cx={shape.start.x} cy={shape.start.y} r="1.8" />
          </g>
        ) : null}
      </svg>
      <div className="course-map__caption">
        <span className="course-map__dot" />
        <span>{label ?? route?.name ?? (world ? "Circuit" : "Loading circuit")}</span>
      </div>
    </div>
  );
}
